import Link from 'next/link';
import Image from 'next/image';
import { useRef } from 'react';
import { useRouter } from 'next/router';
import { motion, useInView, useReducedMotion } from 'motion/react';
import {
  Linkedin,
  Github,
  Instagram,
  Facebook,
  Youtube,
} from 'lucide-react';
import { isFinnishPage, HOME_FI, ABOUT_EN, ABOUT_FI, TEAM_EN, TEAM_FI, CONTACT_EN, CONTACT_FI, WORK_FI, BLOG_FI, SERVICES_FI } from '../../lib/blogPaths';

const ASSET = (id) => `/figma-assets/${id}.png`;

const footerLinksEn = {
  services: [
    { label: 'Software Development', href: '/services/software-development' },
    { label: 'UI/UX Design', href: '/services/ui-ux-design' },
  ],
  company: [
    { label: 'About Us', href: '/about' },
    { label: 'Team', href: '/team' },
    { label: 'Careers', href: '/careers' },
    { label: 'Blog', href: '/blog' },
  ],
  resources: [
    { label: 'Case Studies', href: '/work' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact' },
  ],
};

const footerLinksFi = {
  services: [
    { label: 'Ohjelmistokehitys', href: `${SERVICES_FI}/ohjelmistokehitys` },
    { label: 'UI/UX-suunnittelu', href: `${SERVICES_FI}/ui-ux-suunnittelu` },
  ],
  company: [
    { label: 'Meistä', href: ABOUT_FI },
    { label: 'Tiimi', href: TEAM_FI },
    { label: 'Ura', href: '/rekry' },
    { label: 'Blogi', href: BLOG_FI },
  ],
  resources: [
    { label: 'Työmme', href: WORK_FI },
    { label: 'Blogi', href: BLOG_FI },
    { label: 'Yhteys', href: CONTACT_FI },
  ],
};

const officesEn = [
  { city: 'Turku Office', address: 'Linnankatu 13a B20' },
  { city: 'Helsinki Office', address: 'Kaisaniemenkatu 3 A 5 B' },
];

const officesFi = [
  { city: 'Turun toimisto', address: 'Linnankatu 13a B20' },
  { city: 'Helsingin toimisto', address: 'Kaisaniemenkatu 3 A 5 B' },
];

const socialLinks = [
  { icon: Linkedin, href: 'https://fi.linkedin.com/company/identio', label: 'LinkedIn' },
  { icon: Github, href: 'https://github.com/identiofi', label: 'GitHub' },
  { icon: Instagram, href: 'https://www.instagram.com/identiofi/', label: 'Instagram' },
  { icon: Facebook, href: 'https://www.facebook.com/identiofi', label: 'Facebook' },
  { icon: Youtube, href: 'https://www.youtube.com/channel/UClRlWMyqkrjP0_XudHxgnjA', label: 'YouTube' },
];

const FOOTER_STAGGER = 0.06;

export function Footer() {
  const router = useRouter();
  const ref = useRef(null);
  const reducedMotion = useReducedMotion();
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const staggerDuration = reducedMotion ? 0 : 0.4;
  const staggerDelay = reducedMotion ? 0 : FOOTER_STAGGER;
  const isAboutRoute = router?.pathname === '/[aboutSlug]';
  const isFinnish =
    isFinnishPage(router?.pathname) || (isAboutRoute && router?.asPath === ABOUT_FI);
  const footerLinks = isFinnish ? footerLinksFi : footerLinksEn;
  const offices = isFinnish ? officesFi : officesEn;
  const sectionTitles = isFinnish
    ? { services: 'Palvelut', company: 'Yritys', resources: 'Resurssit', offices: 'Toimistot' }
    : { services: 'Services', company: 'Company', resources: 'Resources', offices: 'Offices' };
  const copyright = isFinnish ? '© 2026 Identio Oy. Kaikki oikeudet pidätetään.' : '© 2026 Identio Oy. All rights reserved.';
  const privacyLink = isFinnish ? { label: 'Tietosuojaseloste', href: '/tietosuojaseloste' } : { label: 'Privacy Policy', href: '/privacy-policy' };

  return (
    <footer ref={ref} className="relative overflow-hidden bg-gradient-to-b from-[#020617] to-[#0a0f1e] text-gray-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-6 py-12 md:py-16 relative z-10 min-w-0">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: reducedMotion ? 0 : 0.5 }}
          className="mb-10 md:mb-12 flex flex-col items-center md:items-start text-center md:text-left"
        >
          <Link
            href={isFinnish ? HOME_FI : '/'}
            className="inline-flex md:-ml-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f1e] rounded"
          >
            <Image
              src="/identio-logo.png"
              alt="Identio"
              width={140}
              height={56}
              className="h-14 w-auto block"
            />
          </Link>
        </motion.div>

        <div className="footer-grid grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 md:gap-8 mb-10 md:mb-12 text-center md:text-left min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: staggerDuration, delay: staggerDelay * 0 }}
            className="footer-column"
          >
            <h3 className="text-white font-bold mb-4 text-base md:text-inherit">{sectionTitles.services}</h3>
            <ul className="space-y-2">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f1e] rounded"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: staggerDuration, delay: staggerDelay * 1 }}
            className="footer-column"
          >
            <h3 className="text-white font-bold mb-4 text-base md:text-inherit">{sectionTitles.company}</h3>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f1e] rounded"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: staggerDuration, delay: staggerDelay * 2 }}
            className="footer-column"
          >
            <h3 className="text-white font-bold mb-4 text-base md:text-inherit">{sectionTitles.resources}</h3>
            <ul className="space-y-2">
              {footerLinks.resources.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f1e] rounded"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: staggerDuration, delay: staggerDelay * 3 }}
            className="footer-column"
          >
            <h3 className="text-white font-bold mb-4 text-base md:text-inherit">{sectionTitles.offices}</h3>
            <div className="space-y-4">
              {offices.map((office) => (
                <div key={office.city}>
                  <p className="text-white text-sm font-medium">{office.city}</p>
                  <p className="text-gray-400 text-sm">{office.address}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: staggerDuration, delay: staggerDelay * 4 }}
          className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-8 pb-8 border-b border-white/10"
        >
          {socialLinks.map((social) => {
            const Icon = social.icon;
            return (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-[44px] min-h-[44px] w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f1e]"
                aria-label={social.label}
              >
                <Icon className="w-5 h-5" />
              </a>
            );
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: staggerDuration, delay: staggerDelay * 5 }}
          className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-300"
        >
          <p>{copyright}</p>
          <Link
            href={privacyLink.href}
            className="text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f1e] rounded"
          >
            {privacyLink.label}
          </Link>
        </motion.div>
      </div>
    </footer>
  );
}
